import { useEffect, type ReactNode } from 'react';
import { useInIndexTable } from './indexTableSurface';
import { useListSurface } from './listSurface';

const TONES = {
  active: 'bg-emerald-100 text-emerald-900 ring-emerald-200',
  pending: 'bg-amber-50 text-amber-800 ring-amber-100',
  blocked: 'bg-red-50 text-red-800 ring-red-100',
  /*
    ORANGE, not amber — a state that needs attention SOON (Building Management's Near
    Expiration, product call 12 Oct 2026). Legal on an index page because it is not the brand
    yellow. orange-800 on orange-50 is 7.4:1 at 11px.
  */
  warning: 'bg-orange-50 text-orange-800 ring-orange-100',
  inactive: 'bg-slate-100 text-slate-700 ring-slate-200',
} as const;

export type StatusTone = keyof typeof TONES;

/**
 * The tone a `status` facet value renders in.
 *
 * Facet keys and tone keys are NOT the same vocabulary and must not be assumed to be. The one
 * worked example was the users facet's `locked`, which mapped to the `blocked` tone; it went
 * with the sign-in lockout on 13 Oct 2026, leaving this an identity map.
 *
 * **`statusToneFor` has no caller, and did not have one before that change either** — the
 * filter rail deliberately imports nothing from this file, which is where a facet value would
 * have been turned into a pill. It is left in place rather than deleted inside an unrelated
 * task, but read it as dead code: the next person to want a facet-key-to-tone mapping should
 * check whether anything calls this before building on it.
 */
const FACET_TONES: Record<string, StatusTone> = {
  active: 'active',
  inactive: 'inactive',
};

/** The tone for a status facet value, or null when the value is not a status at all. */
export function statusToneFor(value: string): StatusTone | null {
  return Object.hasOwn(FACET_TONES, value) ? (FACET_TONES[value] ?? null) : null;
}

/**
 * Status as a tinted pill — small, lowercase, monospaced, barely rounded.
 *
 * DELIBERATE DEVIATION from reference §4 ("no pills/badges in table or card cells; status
 * is tone-coloured plain text"), recorded in CLAUDE.md. Product decision: a pill separates
 * status from ordinary body text at a glance, and the mono lowercase reads as a machine
 * value rather than prose. Do not "fix" this back to plain text without checking with the
 * product owner. Reference §13 already mandates a pill for the audit-log status column, so
 * this leaves the product with one status treatment instead of two.
 *
 * Never colour alone: the word carries the meaning for anyone who cannot distinguish tone.
 * Every token — ground, text, ring — sits one step lighter than it used to on a product
 * call; `active` keeps its one-step-deeper offset from the other three, so the relationship
 * that call established survives the lightening. Contrast at 11px, where the AA floor is
 * still 4.5:1: emerald 8.53:1, amber 6.88:1, red 7.67:1, slate 9.44:1 — every tone reads
 * better than before, because the ground lightened further than the text did.
 */
let warnedAboutAmber = false;

export function StatusText({
  tone,
  children,
}: {
  tone: keyof typeof TONES;
  children: ReactNode;
}) {
  const inIndexTable = useInIndexTable();
  const inList = useListSurface();

  /*
    No yellow on an index page — product call. Amber is the brand colour, and on a list of
    records it reads as an accent the shell put there rather than as a fact about the row.

    Checked in both views, because they render the same `columns`: a tone allowed in one and
    refused in the other would make the two disagree about the same record. Caught in
    development rather than typed away — `pending` stays legal on a profile, in a modal, and
    in a preview table, so the constraint is about WHERE it renders, not about its props.
  */
  useEffect(() => {
    if (!import.meta.env.DEV || tone !== 'pending' || warnedAboutAmber) return;
    if (!inIndexTable && !inList) return;

    warnedAboutAmber = true;
    console.error(
      '<StatusText tone="pending"> is not allowed on an index page — the amber pill is the ' +
        'brand colour and reads as chrome in a list of records. Use "blocked" for a state ' +
        'that stops the record being used. See CLAUDE.md, "Table column conventions".',
    );
  }, [tone, inIndexTable, inList]);

  return (
    <span
      className={`inline-flex items-center rounded-sm px-1.5 py-px font-mono text-[11px] lowercase ring-1 ring-inset ${TONES[tone]}`}
    >
      {children}
    </span>
  );
}
